/*
 * Installierbare Mitarbeiter-App: Service Worker, Installationsangebot (Windows/Android) und Web Push.
 * iPhone/iPad: Installation über Safari „Teilen → Zum Home-Bildschirm“; Push gibt es dort nur in der
 * installierten App (ab iOS/iPadOS 16.4).
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let installEvent: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
  });
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installEvent = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installEvent = null;
    notify();
  });
}

export const onInstallChange = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
export const canPromptInstall = () => installEvent !== null;
export async function promptInstall() {
  if (!installEvent) return false;
  await installEvent.prompt();
  const { outcome } = await installEvent.userChoice;
  installEvent = null;
  notify();
  return outcome === 'accepted';
}

/* ---------- Native Hüllen: Windows-Programm (Electron) und iPhone/iPad-App (Capacitor) ---------- */

export interface NativeNotification {
  title: string;
  body: string;
  url: string;
  tag?: string;
}

/** Vom Windows-Programm bereitgestellt (apps/desktop/src/preload.cjs) */
export interface DesktopBridge {
  version: string;
  notify: (n: NativeNotification) => void;
  onNavigate: (cb: (url: string) => void) => () => void;
  changeServer: () => void;
}

interface PluginHandle {
  remove: () => Promise<void>;
}
interface PushPlugin {
  checkPermissions: () => Promise<{ receive: string }>;
  requestPermissions: () => Promise<{ receive: string }>;
  register: () => Promise<void>;
  unregister?: () => Promise<void>;
  /** Native Brücke liefert den Handle direkt, @capacitor/core als Promise */
  addListener: (event: string, cb: (e: never) => void) => PluginHandle | Promise<PluginHandle>;
}

/** Listener anmelden, unabhängig davon, ob der Handle direkt oder als Promise kommt */
export const listen = <T,>(push: PushPlugin, event: string, cb: (e: T) => void) => Promise.resolve(push.addListener(event, cb as (e: never) => void));
interface CapacitorGlobal {
  isNativePlatform: () => boolean;
  getPlatform: () => string;
  Plugins: { PushNotifications?: PushPlugin };
}

export const desktopBridge = () => (window as { serviceportalDesktop?: DesktopBridge }).serviceportalDesktop;
const capacitor = () => {
  const c = (window as { Capacitor?: CapacitorGlobal }).Capacitor;
  return c?.isNativePlatform?.() ? c : undefined;
};
export const iosPush = () => (capacitor()?.getPlatform() === 'ios' ? capacitor()?.Plugins.PushNotifications : undefined);

/** In welcher Hülle läuft das Portal? */
export const nativeShell = (): 'desktop' | 'ios' | null => (desktopBridge() ? 'desktop' : capacitor()?.getPlatform() === 'ios' ? 'ios' : null);

/** Kennung dieses Geräts (Windows-Programm / iPhone-App) beim Portal */
const NATIVE_KEY = 'sp-native-device';
export function nativeDeviceId() {
  try {
    return localStorage.getItem(NATIVE_KEY);
  } catch {
    return null;
  }
}
export function setNativeDeviceId(id: string | null) {
  try {
    if (id) localStorage.setItem(NATIVE_KEY, id);
    else localStorage.removeItem(NATIVE_KEY);
  } catch {
    /* privates Fenster */
  }
  window.dispatchEvent(new Event('sp-native-device'));
}

/** iPhone/iPad: Berechtigung anfragen und Gerätetoken von Apple holen */
export async function registerApns(): Promise<string> {
  const push = iosPush();
  if (!push) throw new Error('Nur in der iPhone/iPad-App verfügbar');
  let perm = await push.checkPermissions();
  if (perm.receive !== 'granted') perm = await push.requestPermissions();
  if (perm.receive !== 'granted') throw new Error('Mitteilungen sind nicht erlaubt – bitte in den iOS-Einstellungen → Serviceportal → Mitteilungen einschalten.');
  const handles: PluginHandle[] = [];
  try {
    return await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Apple hat kein Gerätetoken geliefert (Internetverbindung?)')), 20_000);
      void listen<{ value: string }>(push, 'registration', (t) => {
        clearTimeout(timer);
        resolve(t.value);
      }).then((h) => handles.push(h));
      void listen<{ error: string }>(push, 'registrationError', (e) => {
        clearTimeout(timer);
        reject(new Error(`Anmeldung bei Apple fehlgeschlagen: ${e.error}`));
      }).then((h) => handles.push(h));
      void push.register();
    });
  } finally {
    handles.forEach((h) => void h.remove());
  }
}

/** Läuft als installierte App (eigenes Fenster bzw. vom Home-Bildschirm)? */
export const isInstalled = () =>
  nativeShell() !== null ||
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.matchMedia?.('(display-mode: window-controls-overlay)').matches ||
  (navigator as { standalone?: boolean }).standalone === true;

export const isWindows = () => /Windows/.test(navigator.userAgent);

export const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Kurze Gerätebeschreibung für die Geräteliste */
export function deviceLabel() {
  const ua = navigator.userAgent;
  const os = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'iPad' : /iPhone/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'Mac' : 'Gerät';
  if (desktopBridge()) return 'Windows (Programm)';
  if (nativeShell() === 'ios') return `${os} (App)`;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return `${os} · ${browser}${isInstalled() ? ' (App)' : ''}`;
}

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Berechtigung anfragen und dieses Gerät beim Push-Dienst anmelden */
export async function subscribePush(publicKey: string) {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'Benachrichtigungen sind für diese Seite blockiert – bitte in den Browser- bzw. Systemeinstellungen erlauben.' : 'Benachrichtigungen wurden nicht erlaubt.');
  const reg = (await navigator.serviceWorker.getRegistration('/')) ?? (await navigator.serviceWorker.register('/sw.js', { scope: '/' }));
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) await existing.unsubscribe().catch(() => undefined);
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
  return sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
}
